import { Link } from "react-router-dom";
import { ArrowRight, Award, ChartColumn, Cloud, Landmark, ShieldCheck, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import Carousel from "../Carousel/Carousel";
import SectionHead from "../SectionHead/SectionHead";
import inepLogo from "../../assets/redesign/logos/inep.svg";
import bertiogaLogo from "../../assets/redesign/logos/bertioga.jpg";
import inepImg from "../../assets/redesign/home/case-inep.jpg";
import gestaoImg from "../../assets/redesign/home/case-gestao.jpg";
import "./CasesCarousel.css";

const bertiogaImg = `${process.env.PUBLIC_URL}/parceiro/bertioga-team.png`;

type Metric = { Icon: typeof Cloud; value: string; label: string };
type Case = {
  key: string;
  image: string;
  logo?: { src: string; alt: string };
  title: string;
  text: string;
  metrics: Metric[];
  cta: { label: string; to: string };
};

// Ordem: o do meio (INEP) é o que abre em destaque.
const CASES: Case[] = [
  {
    key: "bertioga",
    image: bertiogaImg,
    logo: { src: bertiogaLogo, alt: "Prefeitura de Bertioga" },
    title: "Bertioga: referência em educação digital",
    text: "Formação continuada, Google for Education e Gemini na rede municipal, com o Programa Aluno Tutor e alfabetização digital indígena. Bertioga se tornou Município Referência Google for Education no Brasil.",
    metrics: [
      { Icon: Award, value: "Referência", label: "Google for Education" },
      { Icon: Users, value: "Aluno Tutor", label: "protagonismo estudantil" },
      { Icon: Cloud, value: "Projeção", label: "internacional" },
    ],
    cta: { label: "Ver case completo", to: "/parceiro-pedagogico#cases" },
  },
  {
    key: "inep",
    image: inepImg,
    logo: { src: inepLogo, alt: "INEP" },
    title: "Infraestrutura e dados para a educação brasileira",
    text: "Apoiamos o INEP na modernização da infraestrutura em Google Cloud, com ambientes seguros, escaláveis e orientados a dados, habilitando análises, relatórios e serviços de alto impacto para milhões de estudantes.",
    metrics: [
      { Icon: ChartColumn, value: "+8 mi", label: "registros processados" },
      { Icon: Cloud, value: "Ambiente", label: "seguro e escalável" },
      { Icon: ShieldCheck, value: "Governança", label: "e conformidade com LGPD" },
    ],
    cta: { label: "Quero um projeto assim", to: "#contato" },
  },
  {
    key: "public_management",
    image: gestaoImg,
    title: "Transformação digital na gestão pública",
    text: "Modernizamos a operação de secretarias e órgãos públicos com nuvem, colaboração e automação, reduzindo custos e aproximando o serviço público do cidadão.",
    metrics: [
      { Icon: Cloud, value: "Nuvem", label: "migração planejada" },
      { Icon: Users, value: "Colaboração", label: "Google Workspace" },
      { Icon: ShieldCheck, value: "Segurança", label: "dados protegidos" },
    ],
    cta: { label: "Quero um projeto assim", to: "#contato" },
  },
];

export default function CasesCarousel() {
  const { t } = useTranslation();

  return (
    <section className="cz-cases" id="cases" aria-labelledby="cz-cases-title">
      <div className="cz-cases__inner">
        <Carousel
          variant="center"
          arrows="top"
          dots
          initial={1}
          autoplay={8000}
          label={t("home.cases_title", "Cases de Sucesso")}
          head={
            <SectionHead
              id="cz-cases-title"
              title={t("home.cases_title", "Cases de Sucesso")}
              text={t("home.cases_text", "Resultados reais em educação, governo e infraestrutura digital.")}
            />
          }
        >
          {CASES.map((c) => {
            const k = `home.cases.${c.key}`;
            const cta = t(`${k}.cta`, c.cta.label);
            return (
              <article key={c.key} className="cz-case">
                <div className="cz-case__media">
                  <img src={c.image} alt={t(`${k}.title`, c.title)} className="cz-case__img" loading="lazy" />
                </div>
                <div className="cz-case__body">
                  {c.logo ? (
                    <img src={c.logo.src} alt={c.logo.alt} className={`cz-case__logo cz-case__logo--${c.key}`} />
                  ) : (
                    <span className="cz-case__logo-icon" aria-hidden="true">
                      <Landmark size={26} strokeWidth={1.6} />
                    </span>
                  )}
                  <h3 className="cz-case__title">{t(`${k}.title`, c.title)}</h3>
                  <p className="cz-case__text">{t(`${k}.text`, c.text)}</p>
                  <ul className="cz-case__metrics">
                    {c.metrics.map(({ Icon, value, label }, i) => (
                      <li key={i} className="cz-case__metric">
                        <Icon size={20} strokeWidth={1.7} aria-hidden="true" />
                        <span>
                          <strong>{t(`${k}.metrics.${i}.value`, value)}</strong>
                          {t(`${k}.metrics.${i}.label`, label)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {c.cta.to.startsWith("#") ? (
                    <a href={c.cta.to} className="cz-outline-btn cz-outline-btn--sm cz-outline-btn--pink cz-case__cta">
                      {cta}
                      <ArrowRight size={15} aria-hidden="true" />
                    </a>
                  ) : (
                    <Link to={c.cta.to} className="cz-outline-btn cz-outline-btn--sm cz-outline-btn--pink cz-case__cta">
                      {cta}
                      <ArrowRight size={15} aria-hidden="true" />
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </Carousel>
      </div>
    </section>
  );
}
